import algoliasearch from 'algoliasearch/lite';

import instantsearch from 'instantsearch.js';
import { stats, configure, hits } from 'instantsearch.js/es/widgets';
import {
  connectSearchBox,
  connectPagination,
  connectRefinementList,
} from 'instantsearch.js/es/connectors';

import {
  getHitTemplate,
  getEmptyHitsTemplate,
  getSearchBoxTemplate,
  getPaginationTemplate,
  getRefinementTemplate,
  stats as getStatsTemplate,
} from './templates';
import { getSearchQuery, jsonParse, compareMonths, constrain } from './utils';

import Filters from './filters';
import RefinementFiltersManager from './refinement-filters-manager';

const {
  ALGOLIA_APPLICATION_ID,
  ALGOLIA_SEARCH_ONLY_API_KEY,
  ALGOLIA_INDEX_NAME,
  SEARCH_SETTINGS,
} = window.harvard.env;

const containers = {
  searchBox: '#search-searchbox',
  stats: '#search-stats',
  refinement: '#search-refinement',
  refinementFilters: '#search-refinement-filters',
  loading: '#search-loading',
  hits: '#search-hits',
  pagination: '#search-pagination',
};

class SearchPage {
  constructor() {
    this.query = getSearchQuery();

    this.indexName = ALGOLIA_INDEX_NAME;

    const searchSettings = jsonParse(SEARCH_SETTINGS) || {};

    this.refinementAttributes = Object.entries(searchSettings.refinement_attributes);
    this.filters = searchSettings.filters;
    this.overline = searchSettings.overline;
    this.prompt = searchSettings.prompt || 'What can we help you find?';
    this.simple = searchSettings.simple;
    this.paginationSize = searchSettings.pagination_size
      ? constrain(searchSettings.pagination_size, 10, 80)
      : null;

    const searchClient = algoliasearch(ALGOLIA_APPLICATION_ID, ALGOLIA_SEARCH_ONLY_API_KEY);
    this.search = instantsearch({
      indexName: this.indexName,
      initialUiState: { [this.indexName]: { query: this.query } },
      searchClient,
      routing: true,
    });

    this.configure();
    this.search.start();

    this.refinementAttributes.forEach(
      ([_, attribute]) => new Filters(`${containers.refinement}-${attribute}`),
    );
  }

  configure() {
    const customPagination = connectPagination(getPaginationTemplate);
    const customSearchBox = connectSearchBox(getSearchBoxTemplate);
    const customRefinement = connectRefinementList(getRefinementTemplate);
    const refinementFiltersManager = new RefinementFiltersManager(containers.refinementFilters);

    const hitsPerPage = this.paginationSize ? { hitsPerPage: this.paginationSize } : {};

    const customRefinements = this.refinementAttributes.map(([_, attribute]) =>
      customRefinement({
        container: `${containers.refinement}-${attribute} .refinements`,
        filtersManager: refinementFiltersManager,
        attribute,
        limit: 30,
        sortBy: attribute === 'program_months' ? compareMonths : ['name:asc', 'count:desc'],
      }),
    );

    this.search.addWidgets([
      configure({
        distinct: 1,
        facetingAfterDistinct: true,
        filters: this.filters,
        ...hitsPerPage,
      }),

      customSearchBox({
        container: containers.searchBox,
        loadingContainer: containers.loading,
        prompt: this.prompt,
      }),

      stats({
        container: containers.stats,
        templates: {
          text: getStatsTemplate,
        },
      }),

      ...customRefinements,

      hits({
        container: containers.hits,
        templates: {
          empty: getEmptyHitsTemplate,
          item: getHitTemplate(this.overline, this.simple),
        },
      }),

      customPagination({
        container: containers.pagination,
        scrollTo: containers.searchBox,
      }),
    ]);
  }
}

export default SearchPage;
